import React, { useState, useEffect } from 'react';
import {
  Box,
  COLORS,
  Flex,
  Spinner,
  Tag,
  Text,
  Icon,
  useMediaQuery,
} from '@podiumhq/design-system';

import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useChangePage from 'hooks/useChangePage';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';
import { TRIAL_URL } from 'routes/constants';

import useResendVerificationCode from '../../hooks/useResendVerificationCode';
import useSendVerificationCode from '../../hooks/useSendVerificationCode';
import useVerifyAccount from '../../hooks/useVerifyAccount';

import OneTimePasswordInput from './OneTimePasswordInput';

const EnterCode = (props) => {
  const { firstName, lastName, phone, email, setVerifiedAccount, isRoiFlow } =
    useTrialsContext() || {};

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  const { send } = useSendVerificationCode();
  const { resend } = useResendVerificationCode();
  const { verify } = useVerifyAccount();
  const changePage = useChangePage();
  const trackEvent = useTrackAmplitudeEvent();

  const [isSmallerThan650px] = useMediaQuery('(max-width: 650px)');

  useEffect(() => {
    trackEvent('trials.accountVerification.verificationPageViewed');
    const validFields = () => {
      if (!phone || !email || !firstName || !lastName) return false;
      return true;
    };

    if (validFields) {
      send({ phone, email, firstName, lastName })
        .then(() => {
          trackEvent('trials.accountVerification.sendVerificationSuccessful');
        })
        .catch((e) => {
          trackEvent('trials.accountVerification.sendVerificationError');
          if (e.message === 'unexpected_create_signup_token_error')
            return handleResendCode();
          handleError(e);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, email, firstName, lastName]);

  const handleError = (error) => {
    const errorMessage = error.message;
    if (errorMessage.includes('invalid_code'))
      setVerifyError('Invalid code. Please try again.');
    else if (errorMessage.includes('token_expired'))
      setVerifyError(
        'Verification code has expired. Please try resending your code.'
      );
    else if (errorMessage.includes('token_does_not_exist'))
      setVerifyError(
        'Verification code not found. Please try resending your code.'
      );
    else if (errorMessage.includes('limit_exceeded'))
      setVerifyError('Too many attempts. Please try again later.');
    else if (errorMessage.includes('token_expired'))
      setVerifyError(
        'Verification code has expired. Use “resend” to get a new code.'
      );
    else if (
      errorMessage.includes('unexpected_resend_verification_token_error')
    )
      setVerifyError('Error resending verification token. Please try again.');
    else if (errorMessage.includes('token_used'))
      setVerifyError(
        'A verified account already exists for this email and phone number.'
      );
    else if (errorMessage.includes('create_signup_token_error'))
      setVerifyError(
        'Unable to send your verification code. Please try resending.'
      );
    else {
      setVerifyError('An unexpected error occurred. Please try again.');
    }
    setVerifying(false);
    setCode('');
  };

  const handleResendCode = () => {
    setResending(true);
    setVerifyError(null);
    resend(firstName, lastName, email, phone)
      .then(() => {
        trackEvent('trials.accountVerification.resendSuccessful');
        setTimeout(() => {
          setResending(false);
        }, 8000);
      })
      .catch((e) => {
        handleError(e);
        trackEvent('trials.accountVerification.resendNotSuccessful');
        setResending(false);
      });
  };

  const handleVerify = (code) => {
    setVerifying(true);
    verify({ phone, email, code })
      .then(() => {
        setVerifiedAccount(true);
        trackEvent('trials.accountVerification.verificationSuccessful');
        setTimeout(() => changePage(`${TRIAL_URL}/business-info`), 2000);
      })
      .catch((e) => {
        trackEvent('trials.accountVerification.verificationNotSuccessful');
        handleError(e);
        setVerifying(false);
      });
  };

  const handleUpdatePhone = () => {
    trackEvent('trials.accountVerification.updatePhoneNumber');
    if (isRoiFlow) {
      changePage(`${TRIAL_URL}/profile`);
    } else {
      changePage(`${TRIAL_URL}/profile?updatePhone=true`);
    }
  };

  const handleCodeChange = (e) => {
    if (e.length === 4) {
      setTimeout(() => {
        handleVerify(e);
      }, 250);
    }
    if (verifyError) {
      setVerifyError(null);
    }
    setCode(e);
  };

  const returnActions = () => {
    if (resending) {
      return (
        <Tag
          marginTop="-6px"
          height="32px"
          sx={{
            backgroundColor: `${COLORS.silver.light}!important`,
          }}
        >
          <Icon
            type="Check2"
            boxSize="15px"
            color="green.base"
            sx={{ margin: '0 7px 0 1px' }}
          />
          Check your text messages for the code.
        </Tag>
      );
    }

    if (verifying) {
      return (
        <Flex direction="row">
          <Spinner size="xs" />
          <Text
            fontSize="md"
            fontWeight="normal"
            color="grey.lighter"
            padding="2px 0 0 10px"
          >
            Verifying code
          </Text>
        </Flex>
      );
    }

    return (
      <Text fontSize="md" fontWeight="normal">
        Didn’t get the code?
        {isSmallerThan650px && <br />}
        <Box
          onClick={handleResendCode}
          {...actionButtonStyles}
          paddingLeft={isSmallerThan650px ? '0' : '5px'}
        >
          Resend
        </Box>
        or
        <Box onClick={handleUpdatePhone} {...actionButtonStyles}>
          Update your mobile number
        </Box>
      </Text>
    );
  };

  return (
    <div>
      <OneTimePasswordInput
        error={verifyError}
        value={code}
        onChange={handleCodeChange}
        numInputs={4}
      />

      <Box height="24px" />

      <Flex padding="4px 0 12px 0">{returnActions()}</Flex>
    </div>
  );
};

const actionButtonStyles = {
  color: 'blue.base',
  cursor: 'pointer',
  display: 'inline',
  fontSize: '14px',
  marginTop: '1px',
  paddingLeft: '5px',
  paddingRight: '5px',
  lineHeight: '24px',
};

export default EnterCode;
